import React from "react";

import useApp from "hooks/useApp";

import { Alert, Box, Grid, Stack, Typography } from "@mui/material";

import { useMutation } from "react-query";
import { signIn, verifyCode } from "framework/auth";
import { ILoginResponse } from "types/auth";
import { LocalStorage } from "enums/localStorage";
import MuiSnackbar from "components/UI/MuiSnackbar";
import { useAlert } from "contexts/alertContext";
import LoginForm from "./partials/LoginForm";
import { getPermissions } from "framework/permissions";
import { IPermission } from "models/permissions";
import ILogo from "assets/images/logo.png";
import { useLocation } from "react-router-dom";

const Login: React.FC = () => {
  const { push } = useApp();
  const [inputObj, setInputObj] = React.useState<{
    phoneNumber: string;
    code: string;
  }>({
    phoneNumber: "",
    code: "",
  });
  const [showCode, setShowCode] = React.useState<boolean>(false);
  const [errMessage, setErrMessage] = React.useState<string>("");
  const { alert, setAlert } = useAlert();
  const location = useLocation();
   // check if current URL contains ?ByPassTrainer
        const params = new URLSearchParams(location.search);
        const isByPassTrainer = params.has("ByPassTrainer");

  const mutationLogin = useMutation({
    mutationFn: (createInput: any) => {
      return signIn({ data: createInput });
    },
  });

  const mutationGetPermission = useMutation({
    mutationFn: (param: string) => {
      return getPermissions(param);
    },
  });

  const mutationVerifyCode = useMutation({
    mutationFn: (createInput: any) => {
      return verifyCode({ data: createInput });
    },
  });

  const handleChange = (name: string, value: any) => {
    setInputObj({ ...inputObj, [name]: value });
  };
  const onSubmit = async () => {
    if (!showCode) {
      try {
        
       // merge flag into the payload if present
      const payload = isByPassTrainer
        ? { ...inputObj, ByPassTrainer: true }
        : inputObj;

        let res = await mutationLogin.mutateAsync(payload);
        if (!res) {
          setErrMessage("Something went wrong");
          return;
        }
        setShowCode(true);
        setErrMessage("");
        setAlert({
          type: "success",
          message: "Code sent successfully. Please check your phone.",
          open: true,
        });
      } catch (err: any) {
        setErrMessage("Invalid phone number");
      }
    } else {
      try {
        const response: ILoginResponse = await mutationVerifyCode.mutateAsync(
          inputObj
        );
        if (!response?.token) {
          setErrMessage("Invalid code");
          return;
        }
        localStorage.setItem(LocalStorage.ACCESS_TOKEN, response.token);
        localStorage.setItem(LocalStorage.IS_APPROVED_VENDOR, response.isApprovedVendor ? "true" : "false");
        localStorage.setItem(LocalStorage.REFRESH_TOKEN, response.refreshToken);
        localStorage.setItem(LocalStorage.ROLE_NAME, response.roleName.trim());
        localStorage.setItem(
          LocalStorage.COLORS,
          JSON.stringify(response.colors)
        );
        localStorage.setItem(LocalStorage.COMPANY_ID, response.companyId);
        localStorage.setItem(LocalStorage.USER, JSON.stringify(response.user));
        await getUserPermissions(response?.roles[0]);
        
        // Check for enrollment redirect
        const enrollmentRedirect = localStorage.getItem("enrollmentRedirect");
        if (enrollmentRedirect) {
          localStorage.removeItem("enrollmentRedirect");
          push(`/workshop-enrollment/${enrollmentRedirect}`);
        } else {
          push("/");
        }
      } catch (err: any) {
        setErrMessage("Invalid code");
      }
    }
  };

  function reformatPermissionsArray(permissionsArray: IPermission[]) {
    const reformattedPermissions: any = {};

    permissionsArray.forEach((permission) => {
      reformattedPermissions[permission.permissionId.toString()] = {
        id: permission.id,
        roleId: permission.roleId,
        rolePermissionId: permission.rolePermissionId,
        permissionName: permission.permissionName,
        hasFullControl: permission.hasFullControl,
        hasReadPermission: permission.hasReadPermission,
        hasModifyPermission: permission.hasModifyPermission,
        hasDeletePermission: permission.hasDeletePermission,
      };
    });

    return reformattedPermissions;
  }

  const getUserPermissions = async (roleID: string) => {
    try {
      let res = await mutationGetPermission.mutateAsync(`/${roleID}`);
      if (!res) {
        setErrMessage("Something went wrong");
        return;
      }
      const per = reformatPermissionsArray(res);

      localStorage.setItem(LocalStorage.PERMISSIONS, JSON.stringify(per));
      localStorage.setItem(LocalStorage.ROLE_ID, per[1]?.roleId);
    } catch (err: any) {
      setErrMessage("Invalid phone number");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!inputObj.phoneNumber || !inputObj.code) return
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <Box
        component="main"
        sx={{
          display: "flex",
          flex: "1 1 auto",
          minHeight: "100vh",
        }}
      >
        <Grid container sx={{ flex: "1 1 auto" }}>
          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box
              component="header"
              sx={{
                backgroundColor: "background.default",
                alignItems: "center",
                padding: 3,
                px: 5,
              }}
            >
              <Typography variant="h4">Welcome to NEXTLMS</Typography>
              {/* <Typography
                align="center"
                color="inherit"
                sx={{
                  fontSize: "24px",
                  lineHeight: "32px",
                  mb: 1,
                }}
                variant="h2"
              >
                Welcome to{" "}
                <Box component="a" sx={{ color: "#15B79E" }} target="_blank">
                  NEXTLMS
                </Box>
              </Typography> */}
              {/* <Typography align="center" sx={{ mb: 3 }} variant="subtitle1">
                Use your credentials to access your account.
              </Typography> */}
            </Box>
            <Grid
              container
              sx={{
                backgroundColor: "#FFFFFF",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  maxWidth: 550,
                  p: 3,
                  width: "100%",
                }}
              >
                <Stack spacing={1} sx={{ mb: 3 }}>
                  <Typography variant="h4">Login</Typography>
                </Stack>
                <Stack spacing={2} width={"100%"}>
                  <LoginForm
                    onChange={handleChange}
                    onSubmit={onSubmit}
                    showCode={showCode}
                  />
                  {errMessage && (
                    <Alert severity={"error"} variant={"outlined"}>
                      {errMessage}
                    </Alert>
                  )}
                </Stack>
              </Box>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              alignItems: "center",
              background:
                "radial-gradient(50% 50% at 50% 50%, #122647 0%, #090E23 100%)",
              color: "white",
              display: "flex",
              justifyContent: "center",
              "& img": {
                maxWidth: "100%",
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingBlock: "10px",
                }}
              >
                <Box
                  component={"img"}
                  textAlign={"center"}
                  style={{ margin: "0 auto" }}
                  width={"50%"}
                  src={ILogo}
                />
              </div>
            </Box>
          </Grid>
        </Grid>
        <div
          style={{
            position: "fixed",
            bottom: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(227, 222, 220,0.7)",
          }}
        >
          Developed by Emerging Systems
        </div>
      </Box>
      <MuiSnackbar alert={alert!} />
    </div>
  );
};

export default Login;
